"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.NO_POINTS = void 0;
exports.resolveSpendPoints = resolveSpendPoints;
exports.resolveTimePoints = resolveTimePoints;
exports.totalVisitPoints = totalVisitPoints;
const money_1 = require("./money");
/**
 * Loyalty points earning — SPEC §5.5.
 *
 * Two independent rates that STACK: one visit that both spends money and
 * occupies a device earns on both. Either rate at `0` disables that mode, so a
 * venue can run spend-only, time-only, both, or neither without a code change.
 *
 * Pure and I/O-free, mirroring `resolveRefillBonus` in `customers.ts` — the
 * same shape of "config in, resolved result out" so the rules can be tested
 * exhaustively without a database, and so the API and the staff app can preview
 * an award from identical code.
 *
 * Points are whole numbers. Money is bigint millidinars and durations are whole
 * seconds, so every computation here is integer arithmetic — no floats, and no
 * possibility of a fractional point appearing on a customer's balance.
 */
/** Seconds in an hour, as a bigint for exact division against `pointsPerHourPlayed`. */
const SECONDS_PER_HOUR = 3600n;
/** Millidinars in the spend unit the rate is quoted against: 1,000 IQD. */
const MD_PER_RATE_UNIT = money_1.MD_PER_IQD * 1000n;
exports.NO_POINTS = { points: 0, rateApplied: 0 };
/**
 * Points for money actually spent.
 *
 * Deliberately NOT awarded on a refill: a deposit is customer money held on
 * account, so rewarding both the top-up and the spending would pay twice for
 * the same dinar. Points accrue when the balance — or cash — is spent.
 *
 * Partial units are dropped rather than rounded up: spending 1,999 IQD at a
 * rate of 10 earns 10 points, not 20. Rounding up would let a customer farm
 * points by splitting one purchase into many small bills.
 */
function resolveSpendPoints(spent, config) {
    const rate = normaliseRate(config.pointsPerThousandIqd);
    // A refund or a zero bill earns nothing. Guarding here rather than at the
    // call site stops a negative amount minting negative points.
    if (rate === 0 || spent <= 0n)
        return exports.NO_POINTS;
    const units = spent / MD_PER_RATE_UNIT;
    return { points: Number(units * BigInt(rate)), rateApplied: rate };
}
/**
 * Points for time on a device.
 *
 * Applies to ALL play, including free mode. Free-mode PCs unlock to the desktop
 * without billing time, and prepaid packages are paid for up front — under a
 * spend-only scheme both would earn nothing at all despite the customer being
 * in the venue for hours, which is the gap this rate exists to close.
 *
 * Takes `billedSeconds` straight off the closed session: that figure is already
 * pause-adjusted and frozen at close, so recomputing it here could disagree
 * with what the customer was billed.
 *
 * Partial hours are dropped, for the same anti-farming reason as spend.
 */
function resolveTimePoints(billedSeconds, config) {
    const rate = normaliseRate(config.pointsPerHourPlayed);
    if (rate === 0 || !Number.isFinite(billedSeconds) || billedSeconds <= 0)
        return exports.NO_POINTS;
    const hours = BigInt(Math.floor(billedSeconds)) / SECONDS_PER_HOUR;
    return { points: Number(hours * BigInt(rate)), rateApplied: rate };
}
/**
 * What a whole visit earns — the figure to show a customer, since the two
 * modes stack and quoting either alone understates the reward.
 */
function totalVisitPoints(spend, time) {
    return spend.points + time.points;
}
/**
 * A rate is a non-negative whole number of points.
 *
 * Misconfiguration is treated as "disabled" rather than throwing: a bad rate
 * must never be able to block a checkout, because refusing to let a customer
 * pay is a far worse failure than not awarding them points.
 */
function normaliseRate(rate) {
    if (!Number.isFinite(rate) || rate <= 0)
        return 0;
    return Math.floor(rate);
}
//# sourceMappingURL=points.js.map